import { useEffect, useRef } from 'react'
import type { ControlInput } from '../game/types'
import type { PlayerInputRouter } from './input-family'

export type KeyAction =
  | 'throttle'
  | 'brake'
  | 'steer-left'
  | 'steer-right'
  | 'drift'
  | 'boost'
  | 'fire'
  | 'reload'
  | 'pause'
  | 'camera'

export interface ControlActions {
  onPause: () => void
  onCamera: () => void
}

const KEY_BINDINGS: Record<string, KeyAction> = {
  KeyW: 'throttle',
  ArrowUp: 'throttle',
  KeyS: 'brake',
  ArrowDown: 'brake',
  KeyA: 'steer-left',
  ArrowLeft: 'steer-left',
  KeyD: 'steer-right',
  ArrowRight: 'steer-right',
  ShiftLeft: 'drift',
  ShiftRight: 'drift',
  Space: 'fire',
  KeyE: 'boost',
  Escape: 'pause',
  KeyP: 'pause',
  KeyR: 'reload',
  KeyC: 'camera',
}

export function mapKey(code: string): KeyAction | null {
  return KEY_BINDINGS[code] ?? null
}

export function controlsFromPressed(pressed: ReadonlySet<string>): ControlInput {
  const left = pressed.has('KeyA') || pressed.has('ArrowLeft')
  const right = pressed.has('KeyD') || pressed.has('ArrowRight')
  return {
    throttle: pressed.has('KeyW') || pressed.has('ArrowUp') ? 1 : 0,
    brake: pressed.has('KeyS') || pressed.has('ArrowDown') ? 1 : 0,
    steer: (left ? 1 : 0) - (right ? 1 : 0),
    drift: pressed.has('ShiftLeft') || pressed.has('ShiftRight'),
    boost: pressed.has('KeyE'),
    fire: pressed.has('Space'),
    reload: pressed.has('KeyR'),
  }
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(target.tagName)
}

export function useControls(router: PlayerInputRouter, enabled: boolean, actions: ControlActions): void {
  const actionsRef = useRef(actions)
  actionsRef.current = actions
  useEffect(() => {
    if (!enabled) {
      router.clear('keyboard')
      return
    }
    const pressed = new Set<string>()
    const update = () => router.update('keyboard', controlsFromPressed(pressed))
    const clear = () => {
      pressed.clear()
      router.clear('keyboard')
    }
    const onKeyDown = (event: KeyboardEvent) => {
      const action = mapKey(event.code)
      if (!action || isInteractiveTarget(event.target)) return
      event.preventDefault()
      if (action === 'pause' || action === 'camera') {
        if (event.repeat) return
        if (action === 'pause') actionsRef.current.onPause()
        if (action === 'camera') actionsRef.current.onCamera()
        return
      }
      pressed.add(event.code)
      update()
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (!mapKey(event.code)) return
      pressed.delete(event.code)
      update()
    }
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') clear()
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', clear)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', clear)
      document.removeEventListener('visibilitychange', onVisibility)
      clear()
    }
  }, [enabled, router])
}
